import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { AppBannerSetting, Severity } from 'models/denthub-settings';
import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

export type BannerFormProps = {
    sectionTitle: string;
    bannerSettings: AppBannerSetting;
    onToggle: (value: boolean) => void;
    onTitleChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    onMessageChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    onSeveritySelect: (severity: Severity) => void;
    onSave: () => void;
};

const SeverityValues = [Severity.error, Severity.warning, Severity.info, Severity.success] as const;

const BannerForm: FC<BannerFormProps> = ({
    sectionTitle,
    bannerSettings,
    onToggle,
    onTitleChange,
    onMessageChange,
    onSeveritySelect,
    onSave,
}) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    return (
        <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h2" mb={1}>
                {sectionTitle}
            </Typography>
            <Box mb={1}>
                <FormControlLabel
                    control={
                        <Switch
                            color="secondary"
                            checked={bannerSettings.isEnabled || false}
                            onChange={(_, value) => onToggle(value)}
                        />
                    }
                    label={translate('app-settings:ENABLE_SETTING')}
                />
            </Box>
            <Box mb={1}>
                <TextField
                    value={bannerSettings.title || ''}
                    onChange={onTitleChange}
                    fullWidth
                    label={translate('app-settings:AUTH_APP_BANNER_TITLE')}
                />
            </Box>
            <Box mb={1}>
                <TextField
                    value={bannerSettings.message || ''}
                    onChange={onMessageChange}
                    fullWidth
                    multiline
                    rows={4}
                    label={translate('app-settings:AUTH_APP_BANNER_MESSAGE')}
                />
            </Box>
            <Box mb={1}>
                <FormControl margin="dense" fullWidth style={{ minWidth: 150 }}>
                    <InputLabel>{translate('app-settings:SEVERITY')}</InputLabel>
                    <Select
                        label={translate('app-settings:SEVERITY')}
                        value={bannerSettings.severity || ''}
                        onChange={(event: SelectChangeEvent<Severity>) =>
                            onSeveritySelect(event.target.value as Severity)
                        }
                    >
                        <MenuItem disabled hidden value="">
                            {translate('app-settings:SELECT_SEVERITY')}
                        </MenuItem>
                        {SeverityValues.map((severity: Severity) => (
                            <MenuItem key={severity} value={severity}>
                                {severity}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box>
                <Button
                    disabled={bannerSettings.isEnabled && (!bannerSettings.message || !bannerSettings.severity)}
                    variant="contained"
                    onClick={onSave}
                >
                    {translate('common:SAVE')}
                </Button>
            </Box>
        </Paper>
    );
};

export default BannerForm;
